// slices/lectures/pendingLecturesSlice.ts
import {
  createSlice,
  PayloadAction,
  createEntityAdapter,
} from '@reduxjs/toolkit';
import { RootState } from '../../store/store';
import { logout } from '../auth/authSlice';

export type PendingLectureStatus = 'CREATING' | 'UPDATING' | 'FAILED';

export interface PendingLecture {
  id: string;
  examId: string;
  originalFilename: string;
  status: PendingLectureStatus;
  error?: string;
  mappingStartedAt: string;
  finalLectureId?: string;
  name?: string;
  date?: string | null;
  s3Key?: string;
  existingLectureId?: string;
}

// Create the entity adapter
// Sort by when the upload started, newest first might be useful
const pendingLecturesAdapter = createEntityAdapter<PendingLecture>({
  sortComparer: (a, b) =>
    new Date(b.mappingStartedAt).getTime() -
    new Date(a.mappingStartedAt).getTime(),
});

interface PendingLecturesState
  extends ReturnType<typeof pendingLecturesAdapter.getInitialState> {}

// Define the initial state
const initialState: PendingLecturesState =
  pendingLecturesAdapter.getInitialState({});

export const pendingLecturesSlice = createSlice({
  name: 'pendingLectures',
  initialState,
  reducers: {
    addPendingLecture: (
      state,
      action: PayloadAction<{
        pendingId: string;
        name?: string;
        date?: string | null;
        status: PendingLectureStatus;
        examId: string;
        originalFilename: string;
        s3Key?: string;
        existingLectureId?: string;
      }>
    ) => {
      const {
        pendingId,
        examId,
        originalFilename,
        s3Key,
        existingLectureId,
        status,
        name,
        date,
      } = action.payload;
      const newPendingLecture: PendingLecture = {
        id: pendingId,
        examId,
        originalFilename,
        status,
        mappingStartedAt: new Date().toISOString(),
        s3Key,
        existingLectureId,
        name,
        date,
      };
      pendingLecturesAdapter.addOne(state, newPendingLecture);
    },

    updatePendingLecture: (
      state,
      action: PayloadAction<{
        id: string;
        name?: string;
        date?: string | null;
        status?: PendingLectureStatus;
        error?: string;
        finalLectureId?: string;
        s3Key?: string;
        existingLectureId?: string;
      }>
    ) => {
      const { id, ...changes } = action.payload;
      const filteredChanges = Object.fromEntries(
        Object.entries(changes).filter(([_, value]) => value !== undefined)
      );
      pendingLecturesAdapter.updateOne(state, {
        id,
        changes: filteredChanges,
      });
    },

    removePendingLecture: (state, action: PayloadAction<string>) => {
      pendingLecturesAdapter.removeOne(state, action.payload);
    },

    clearPendingLectures: (state) => {
      pendingLecturesAdapter.removeAll(state);
    },
  },
  extraReducers: (builder) => {
    builder.addCase(logout, (state) => {
      pendingLecturesAdapter.removeAll(state);
    });
  },
});

export const {
  addPendingLecture,
  updatePendingLecture,
  removePendingLecture,
  clearPendingLectures,
} = pendingLecturesSlice.actions;

// Export selectors
export const pendingLecturesSelectors = pendingLecturesAdapter.getSelectors(
  (state: RootState) =>
    state.pendingLectures || pendingLecturesAdapter.getInitialState()
);

export default pendingLecturesSlice.reducer;
